<template>
  <header class="vault-navbar">
    <div class="navbar-inner">
      <div class="navbar-left">
        <a href="/" class="logo-link">
          <img src="@/assets/logos/vault-icons.png" alt="Vault" class="vault-logo" />
        </a>
      </div>

      <nav class="navbar-pill">
        <button
          class="pill-item"
          :class="{ active: activeTab === 'icons' && !toolsOpen && !contentToolsOpen }"
          @click="selectTab('icons')"
        >
          Vault
        </button>

        <div class="pill-dropdown" v-click-outside="closeTools">
          <button
            class="pill-item dropdown-toggle"
            :class="{ active: toolsOpen }"
            @click="toolsOpen = !toolsOpen"
          >
            MC Tools
            <img
              src="@/assets/icons/dropdown-arrow.svg"
              alt=""
              class="dropdown-arrow"
              :class="{ flipped: toolsOpen }"
            />
          </button>
          <div v-if="toolsOpen" class="dropdown-menu">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              class="dropdown-item"
              :class="{ active: activeTab === tab.id }"
              @click="selectTab(tab.id)"
            >
              <img src="@/assets/logos/vault-icons.png" alt="" class="dropdown-icon" />
              {{ tab.name }}
            </button>
          </div>
        </div>
        <button class="pill-item coming-soon" aria-disabled="true" data-tooltip="Coming Soon" @click.prevent>My Vaults</button>
        <button class="pill-item coming-soon" aria-disabled="true" data-tooltip="Coming Soon" @click.prevent>Vault+</button>
      </nav>

      <div class="navbar-right">
        <a href="#" class="signup-link coming-soon" aria-disabled="true" data-tooltip="Coming Soon" @click.prevent>Sign Up</a>
        <button class="get-vault-btn coming-soon" aria-disabled="true" data-tooltip="Coming Soon" @click.prevent>
          Get Vault<span class="plus">+</span>
        </button>
        <a :href="mciconsUrl" target="_blank" class="ccleaf-link" aria-label="Powered by ccLeaf">
          <img src="@/assets/logos/ccleaf_logo.png" alt="ccLeaf" class="ccleaf-logo" />
        </a>
      </div>
    </div>

  </header>
  <Teleport to="body">
    <div v-if="showBanner" class="announcement-banner mx-auto">
      <span class="banner-text">We&rsquo;re still in the process of a big release to Vault!<br>All icons and texture from 26.x has been added! Enjoy our new branding!</span>
      <button class="banner-dismiss" @click="dismissBanner">&times;</button>
    </div>
  </Teleport>
</template>

<script>
import { getCurrentInstance, ref, onMounted } from 'vue';

const clickOutside = {
  beforeMount(el, binding) {
    el._clickOutside = (event) => {
      if (!el.contains(event.target)) binding.value(event);
    };
    document.addEventListener('click', el._clickOutside);
  },
  unmounted(el) {
    document.removeEventListener('click', el._clickOutside);
  },
};

export default {
    name: 'AppHeader',
    directives: { 'click-outside': clickOutside },
    props: {
        activeTab: {
            type: String,
            default: 'icons'
        }
    },
    emits: ['tab-changed'],
    setup(props, { emit }) {
        const instance = getCurrentInstance();
        const mciconsUrl = instance.appContext.config.globalProperties.$mciconsUrl;

        const tabs = [
          { id: 'icons', name: 'Icons' },
          { id: 'textures', name: 'Textures' },
          { id: 'skins', name: 'Skins' },
        ];

        const toolsOpen = ref(false);
        const contentToolsOpen = ref(false);
        const showBanner = ref(false);

        const selectTab = (tabId) => {
          emit('tab-changed', tabId);
          toolsOpen.value = false;
          contentToolsOpen.value = false;
        };

        const closeTools = () => { toolsOpen.value = false; };
        const closeContentTools = () => { contentToolsOpen.value = false; };
        const openVaults = () => {};
        const openPlus = () => {};
        const openSignup = () => {};

        onMounted(() => {
          if (localStorage.getItem('banner_dismissed') !== 'true') {
            showBanner.value = true;
          }
        });

        const dismissBanner = () => {
          showBanner.value = false;
          localStorage.setItem('banner_dismissed', 'true');
        };

        return {
          mciconsUrl,
          tabs,
          toolsOpen,
          contentToolsOpen,
          showBanner,
          selectTab,
          closeTools,
          closeContentTools,
          openVaults,
          openPlus,
          openSignup,
          dismissBanner,
        };
    }
}
</script>

<style scoped>
.vault-navbar {
  position: relative;
  width: 100%;
  background-color: rgba(8, 8, 8, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-bottom: 2px solid rgba(34, 34, 34, 0.75);
  z-index: 50;
}

.navbar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  max-width: 1400px;
  margin: 0 auto;
  gap: 24px;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 15px;
  width: 245px;
  flex-shrink: 0;
}

.logo-link {
  display: inline-flex;
  align-items: center;
}

.vault-logo {
  height: 35px;
  width: auto;
  object-fit: contain;
}

.navbar-pill {
  display: flex;
  align-items: center;
  gap: 30px;
  padding: 10px 16px;
  background-color: rgba(20, 20, 20, 0.9);
  border-radius: 15px;
  flex-shrink: 0;
}

.pill-item {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  color: #a6a6a6;
  background: transparent;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.pill-item:hover { color: #fff; }

.pill-item.active {
  color: #fff;
  background-color: #303030;
}

.pill-dropdown {
  position: relative;
}

.dropdown-arrow {
  width: 8px;
  height: 6px;
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

.dropdown-arrow.flipped {
  transform: rotate(0deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 140px;
  background-color: rgba(20, 20, 20, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  z-index: 60;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: #a6a6a6;
  background: transparent;
  border: none;
  border-radius: 6px;
  text-align: left;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.dropdown-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.dropdown-item:hover {
  color: #fff;
  background-color: #303030;
}

.dropdown-item.active {
  color: #fff;
  background-color: #303030;
}

.navbar-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
  width: 245px;
  flex-shrink: 0;
}

.signup-link {
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  color: #a6a6a6;
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.2s ease;
}

.signup-link:hover { color: #fff; }

.get-vault-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 37px;
  padding: 0 18px;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #000;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
  background:
    radial-gradient(
      ellipse 80% 160% at 28% 18%,
      rgba(250, 255, 255, 1) 0%,
      rgba(251, 249, 192, 1) 8.6%,
      rgba(253, 244, 130, 1) 17.3%,
      rgba(253, 242, 98, 1) 21.6%,
      rgba(254, 239, 67, 1) 26%,
      rgba(254, 237, 35, 1) 30.3%,
      rgba(255, 235, 20, 1) 32.5%,
      rgba(255, 234, 4, 1) 34.6%,
      rgba(255, 159, 4, 1) 77.9%,
      rgba(255, 98, 0, 1) 98.1%
    );
  transition: transform 0.15s ease, filter 0.15s ease;
}

.get-vault-btn:hover {
  transform: scale(1.02);
  filter: brightness(1.05);
}

.get-vault-btn .plus {
  font-weight: 700;
  margin-left: 1px;
}

.ccleaf-link {
  display: inline-flex;
  align-items: center;
  opacity: 0.9;
  transition: opacity 0.2s ease;
}

.ccleaf-link:hover { opacity: 1; }

.ccleaf-logo {
  height: 33px;
  width: auto;
  object-fit: contain;
}

.coming-soon {
  position: relative;
  cursor: not-allowed !important;
  filter: grayscale(1);
  opacity: 0.55;
}

.coming-soon:hover {
  color: #a6a6a6 !important;
  background-color: transparent !important;
  transform: none !important;
  filter: grayscale(1) !important;
}

.get-vault-btn.coming-soon {
  background: linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%);
  color: #d4a53a;
  border-color: rgba(212, 165, 58, 0.45);
  filter: none;
  opacity: 0.85;
}

.get-vault-btn.coming-soon .plus {
  color: #e8b94a;
}

.get-vault-btn.coming-soon:hover {
  background: linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%) !important;
  color: #d4a53a !important;
  filter: none !important;
}

.coming-soon::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  padding: 5px 10px;
  background-color: rgba(20, 20, 20, 0.98);
  color: #fff;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.2px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 100;
  filter: none;
}

.coming-soon::before {
  content: '';
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-bottom-color: rgba(20, 20, 20, 0.98);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 100;
  filter: none;
}

.coming-soon:hover::after,
.coming-soon:hover::before {
  opacity: 1;
}

@media (max-width: 900px) {
  .navbar-inner { padding: 12px 16px; gap: 12px; }
  .navbar-left, .navbar-right { width: auto; }
  .navbar-pill { gap: 16px; padding: 8px 12px; }
  .pill-item { font-size: 14px; }
  .signup-link { font-size: 14px; }
  .get-vault-btn { font-size: 14px; height: 32px; padding: 0 14px; }
}

@media (max-width: 640px) {
  .navbar-inner { flex-wrap: wrap; }
  .navbar-pill { order: 3; width: 100%; justify-content: space-between; gap: 8px; }
  .signup-link { display: none; }
}

.announcement-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 75px;
  background:
    linear-gradient(#000, #000) padding-box,
    linear-gradient(129deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(168, 168, 168, 0.02) 7.69%,
      rgba(255, 255, 255, 1) 42.79%,
      rgba(154, 154, 154, 0.02) 83.17%,
      rgba(255, 255, 255, 1) 92.79%
    ) border-box;
  padding: 1.35rem 4.5rem 1.35rem 3rem;
  position: fixed;
  bottom: 1rem;
  left: 50%;
  z-index: 999;
  width: max-content;
  max-width: calc(100% - 2rem);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
  opacity: 0;
  transform: translate(-50%, 120%) scale(0.9);
  animation: bannerSpringUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s forwards;
}

@keyframes bannerSpringUp {
  0% { opacity: 0; transform: translate(-50%, 120%) scale(0.9); }
  60% { opacity: 1; transform: translate(-50%, -8%) scale(1.03); }
  80% { transform: translate(-50%, 2%) scale(0.99); }
  100% { opacity: 1; transform: translate(-50%, 0) scale(1); }
}

.banner-text {
  color: #c7c7c8;
  font-size: 1.2rem;
  font-family: 'Roboto', sans-serif;
  image-rendering: pixelated;
  flex: 1;
  padding-left: 3px;
  text-align: left;
  opacity: 0;
  animation: bannerFadeIn 0.8s ease-out 0.3s forwards;
}

@keyframes bannerFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.banner-dismiss {
  position: absolute;
  top: 50%;
  right: 1.5rem;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #c7c7c8;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 0 0.6rem;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.banner-dismiss:hover { color: #00e5ff; }

@media (max-width: 767px) {
  .banner-text { font-size: 1.05rem; }
}
</style>
